import React, { useEffect, useState } from "react";
import { Star, Trash, Check } from "@phosphor-icons/react";
import InlineShareButton from "../InlineShareButton";
import { shareOrigin } from "../../lib/shareOrigin";

const RADIUS_OPTIONS = [5, 10, 25, 50];

export default function SavedLocationItem({ location, onUpdate, onRemove }) {
  const l = location;
  // Label edits stay in a draft until explicitly saved (Enter or ✓ button).
  const [draft, setDraft] = useState(l.label);
  useEffect(() => { setDraft(l.label); }, [l.label]);
  const dirty = draft !== l.label;

  const saveLabel = () => {
    const label = draft.trim() || "Location";
    setDraft(label);
    onUpdate(l.id, { label });
  };

  return (
    <li
      data-testid={`saved-location-${l.id}`}
      className="px-5 py-3 flex items-start justify-between gap-3 hover:bg-tac-surface/40"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Star size={11} weight="fill" className="text-tac-primary" />
          <input
            data-testid={`label-input-${l.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveLabel(); }}
            className="bg-transparent border-b border-transparent focus:border-tac-borderBright text-sm text-tac-text font-medium outline-none flex-1 min-w-0"
          />
          {dirty && (
            <button
              data-testid={`save-label-${l.id}`}
              onClick={saveLabel}
              className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-tac-primary text-white hover:opacity-90 transition-opacity shrink-0"
              title="Save name"
            >
              <Check size={11} weight="bold" /> Save
            </button>
          )}
        </div>
        <div className="text-[15px] font-mono-tac tabular text-tac-faint">
          {l.lat.toFixed(4)}°, {l.lon.toFixed(4)}°
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[15px] uppercase tracking-wider font-bold text-tac-dim mr-1">
            Alert radius:
          </span>
          {RADIUS_OPTIONS.map((mi) => (
            <button
              key={mi}
              data-testid={`radius-${l.id}-${mi}`}
              onClick={() => onUpdate(l.id, { radius_mi: mi })}
              className={`px-2 py-0.5 text-[10px] font-bold tabular rounded-sm border transition-colors ${
                l.radius_mi === mi
                  ? "bg-tac-primary text-white border-tac-primary"
                  : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text"
              }`}
            >
              {mi} mi
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <InlineShareButton
          variant="compact"
          testid={`share-status-${l.id}`}
          url={`${shareOrigin()}/api/share/status?lat=${l.lat}&lon=${l.lon}&label=${encodeURIComponent(l.label || "My Location")}`}
          title={`My Fire Status — ${l.label}`}
          caption={[
            `☀️ ${new Date().getHours() < 12 ? "MORNING" : new Date().getHours() < 17 ? "AFTERNOON" : "EVENING"} FIRE CHECK-IN — ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase()}`,
            `📍 ${(l.label || "My Location").toUpperCase()} — nearest fire + exact distance on the card below.`,
            "",
            "How close is one to YOUR place? Check yours:",
            `${shareOrigin()}/api/share/status?lat=${l.lat}&lon=${l.lon}&label=${encodeURIComponent(l.label || "My Location")}`,
          ].join("\n")}
          text={`My fire status at ${l.label} via Okanogan Highlands Firewatch`}
        />
        <button
          data-testid={`remove-${l.id}`}
          onClick={() => onRemove(l.id)}
          className="text-tac-faint hover:text-tac-danger p-1.5 rounded-sm transition-colors"
          aria-label="Remove"
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>
    </li>
  );
}
